// 도구 막대의 펼침 메뉴(공용) — 단추 하나 + 그 아래 떠오르는 판.
//
// 닫히는 경우: 단추를 다시 누름 · 판 바깥을 누름 · Esc. 판 안의 항목이 `close()` 를 불러 닫을 수도 있다
// (동작 항목은 닫고, 켬/끔·목록 선택처럼 결과를 눈으로 확인해야 하는 항목은 열어 둔다).
//
// 🔴 Esc 는 원격으로도 가는 키다 — 메뉴가 열려 있을 때만 가로채고(`stopPropagation`), 닫힌 뒤엔 손대지 않는다.
import { useEffect, useRef, useState, type ReactNode } from 'react';

export function BarMenu({
  label,
  title,
  badge,
  onClosed,
  children,
}: {
  /** 단추 안에 그릴 것(아이콘 등). */
  label: ReactNode;
  /** 단추 풍선 도움말. */
  title: string;
  /** 단추 모서리 숫자(0 이하면 안 그린다) — 메뉴 안에 숨은 항목의 알림(예: 안 읽은 채팅)을 밖에서 보이게. */
  badge?: number;
  /** 판이 닫힌 뒤 — 키 입력 포커스를 원격 화면으로 되돌리는 데 쓴다. */
  onClosed?: () => void;
  /** 판 내용. `close` 를 부르면 판이 닫힌다. */
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  const close = () => {
    setOpen(false);
    onClosed?.();
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) {
        close();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
    // close 는 매 렌더 새로 만들어지지만 하는 일은 같다(상태 setter + 콜백) — open 이 바뀔 때만 다시 건다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <span className="bar-menu" ref={wrapRef}>
      <button
        className={`ghost${open ? ' on' : ''}`}
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        {label}
        {!!badge && badge > 0 && <span className="badge">{badge}</span>}
      </button>
      {open && (
        <div className="bar-menu-panel" role="menu">
          {children(close)}
        </div>
      )}
    </span>
  );
}
